import type { CostBreakdownFormValues, CostBreakdownProductsFormValues } from "../schemas"

import type { GoodsReceipt } from "@/graphql/codegen/graphql"
import { getGoodsReceiptPurchaseOrderLines } from "@/screens/WarehouseReceiving/utils"
import { roundToPrecision } from "@/utils/number"

interface TransformFromPayloadResult {
  grCostBreakdown: CostBreakdownFormValues
  productsCostBreakdown: CostBreakdownProductsFormValues
}

export const transformGoodsReceiptToFormData = (goodsReceipt: GoodsReceipt): TransformFromPayloadResult => {
  const costBreakdown = goodsReceipt.costBreakdown

  if (!costBreakdown) {
    throw new Error("No cost breakdown data found")
  }

  // Transform GR-level cost breakdown
  const grCostBreakdown: CostBreakdownFormValues = {
    importFees: costBreakdown.importFees ?? null,
    insurance: costBreakdown.insurance ?? null,
    mpf: costBreakdown.mpf ?? null,
    hmf: costBreakdown.hmf ?? null,
    sharedGrCost: costBreakdown.sharedGrCost ?? null,

    shippingCosts: (costBreakdown.shippingCosts ?? []).map((cost) => ({
      shippingType: cost.shippingType,
      cost: cost.cost ?? null,
    })),

    atypicalSurcharges: (costBreakdown.atypicalSurcharges ?? []).map((surcharge) => ({
      description: surcharge.description || "",
      amount: surcharge.amount ?? null,
    })),
  }

  // Use the same ordering as the table to ensure row.index matches form productIndex
  const purchaseOrderLines = getGoodsReceiptPurchaseOrderLines(goodsReceipt)
  const lineCostBreakdowns = costBreakdown.lineCostBreakdowns ?? []

  const products = purchaseOrderLines.map((purchaseOrderLine) => {
    // Find the corresponding line cost breakdown for this purchase order line
    const lineCostBreakdown = lineCostBreakdowns.find(
      (line) => line.goodsReceiptProductDetailId === purchaseOrderLine.goodsReceiptLine?.goodsReceiptProductDetailId
    )

    return {
      pricePerUom: lineCostBreakdown?.pricePerUom ?? null,
      bareCostPerUom: lineCostBreakdown?.bareCostPerUom ?? null,
      totalBareCost: lineCostBreakdown?.totalBareCost ?? null,
      insurance: lineCostBreakdown?.insurance ?? null,

      duties: {
        amount: lineCostBreakdown?.duties ?? null,
        percentage: null, // This will be calculated by the auto-calculation logic
      },

      mpf: {
        amount: lineCostBreakdown?.mpf ?? null,
        percentage: null, // This will be calculated by the auto-calculation logic
      },

      hmf: {
        amount: lineCostBreakdown?.hmf ?? null,
        percentage: null, // This will be calculated by the auto-calculation logic
      },

      allocatedGrCostPercentage: lineCostBreakdown?.allocatedGrCostPercentage ?? null,
      allocatedGrCostAmount: lineCostBreakdown?.allocatedGrCostAmount ?? null,

      lastLaidDownCostPerUom: lineCostBreakdown?.lastLaidDownCostPerUom ?? null,
      totalLaidDownCostPerUom: lineCostBreakdown?.totalLaidDownCostPerUom
        ? roundToPrecision(lineCostBreakdown.totalLaidDownCostPerUom, 2)
        : null,

      isCompleted: lineCostBreakdown?.isCompleted ?? false,

      tariffs: (lineCostBreakdown?.tariffs ?? []).map((tariff) => ({
        amount: tariff.amount ?? null,
        percentage: null, // This will be calculated by the auto-calculation logic
      })),

      atypicalSurcharges: (lineCostBreakdown?.atypicalSurcharges ?? []).map((surcharge) => ({
        type: surcharge.type,
        description: surcharge.description || "",
        amount: surcharge.amount ?? null,
      })),
    }
  })

  const productsCostBreakdown: CostBreakdownProductsFormValues = {
    products,
    sharedGrCost: costBreakdown.sharedGrCost ?? null,
  }

  return {
    grCostBreakdown,
    productsCostBreakdown,
  }
}
